import React, { useEffect, useMemo, useState } from 'react';
import StickerCell from './StickerCell';
import { ALBUM_SECTIONS } from '../albumData';
import { resolveStickerStyle, getBadgeStyle } from '../albumColors';
import {
  getSectionCountries,
  countryForSticker,
  mockPlayerName,
  mockPhotoStyle,
} from '../albumDecorations';

/* ════════════════════════════════════════════════════════════════════
   Helpers
   ════════════════════════════════════════════════════════════════════ */

function isHidden(n) {
  // CC slots 995-1000 were retired; filter them out of every grid view.
  return n >= 995 && n <= 1000;
}
function sliceRange(stickers, from, to) {
  return stickers.filter(s => s.n >= from && s.n <= to && !isHidden(s.n));
}
function sliceSection(stickers, section) {
  return sliceRange(stickers, section.from, section.to);
}
// Cuando hay un trade contextual (TRADE_REVIEW o banner activo), recortamos
// los layouts a las secciones/países que tienen al menos una figurita
// participante en el trade. El resto es ruido.
function rangeParticipates(participating, from, to) {
  if (!participating || participating.size === 0) return true;
  for (const n of participating) {
    if (n >= from && n <= to) return true;
  }
  return false;
}
function ownedInSlice(slice) {
  return slice.filter(s => s.owned).length;
}

/* ════════════════════════════════════════════════════════════════════
   Reusable building blocks
   ════════════════════════════════════════════════════════════════════ */

function StickerBlock({ stickers, columns, theme, cellProps, padding = 'p-3' }) {
  const visible = stickers.filter(s => !isHidden(s.n));
  return (
    <div
      className={padding}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: 3,
        backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)',
      }}
    >
      {visible.map(s => (
        <StickerCell
          key={s.n}
          sticker={s}
          {...cellProps}
          onClick={() => cellProps.onCellClick(s)}
        />
      ))}
    </div>
  );
}

function SectionHeader({ section, slice, accent, muted, dense = false }) {
  const owned = ownedInSlice(slice);
  const total = slice.length;
  const pct = total === 0 ? 0 : Math.round((owned / total) * 100);
  return (
    <div className={`flex items-baseline gap-3 ${dense ? 'px-3 pt-3 pb-1' : 'px-3 pt-5 pb-2'}`}>
      <span
        className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest shrink-0"
        style={{ backgroundColor: `${accent}22`, color: accent }}
      >
        {section.label}
      </span>
      <span className="text-xs font-medium truncate" style={{ color: muted }}>
        {section.subtitle}
      </span>
      <span className="ml-auto text-[10px] tabular-nums shrink-0" style={{ color: muted }}>
        {owned}/{total} · {pct}%
      </span>
    </div>
  );
}

/* Country header: small flag-color band + name + code + progress */
function CountryHeader({ country, slice, muted, dense = false }) {
  const owned = ownedInSlice(slice);
  const total = slice.length;
  const pct = total === 0 ? 0 : Math.round((owned / total) * 100);
  return (
    <div className={`flex items-center gap-2.5 ${dense ? 'px-3 pt-2 pb-1' : 'px-3 pt-4 pb-1.5'}`}>
      <span className="w-1 h-4 rounded-sm shrink-0" style={{ backgroundColor: country.color }} />
      <span className="text-[11px] font-black uppercase tracking-wide truncate" style={{ color: country.color }}>
        {country.code}
      </span>
      <span className="text-xs font-medium truncate" style={{ color: muted }}>
        {country.name}
      </span>
      <span className="ml-auto text-[10px] tabular-nums shrink-0" style={{ color: muted }}>
        {owned}/{total} · {pct}%
      </span>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Layouts 0–5 (existing)
   ════════════════════════════════════════════════════════════════════ */

function FlatGrid({ stickers, columns, theme, cellProps }) {
  return <StickerBlock stickers={stickers} columns={columns} theme={theme} cellProps={cellProps} />;
}

function SectionsLayout({ stickers, columns, theme, cellProps, accent, muted }) {
  const participating = cellProps?.participatingStickers;
  return (
    <div>
      {ALBUM_SECTIONS.filter(s => rangeParticipates(participating, s.from, s.to)).map(section => {
        const slice = sliceSection(stickers, section);
        if (slice.length === 0) return null;
        return (
          <div key={section.id}>
            <SectionHeader section={section} slice={slice} accent={accent} muted={muted} />
            <StickerBlock stickers={slice} columns={columns} theme={theme} cellProps={cellProps} />
          </div>
        );
      })}
    </div>
  );
}

function TabsLayout({ stickers, columns, theme, cellProps, accent, muted, activeId, setActiveId }) {
  const participating = cellProps?.participatingStickers;
  const sections = ALBUM_SECTIONS.filter(s => rangeParticipates(participating, s.from, s.to));
  const active = sections.find(s => s.id === activeId) || sections[0];
  const slice = sliceSection(stickers, active);

  return (
    <div>
      <div
        className="flex items-center gap-1 overflow-x-auto hide-scrollbar px-3 pt-3 pb-2"
        style={{ borderBottom: `1px solid ${muted}22` }}
      >
        {sections.map(section => {
          const isActive = section.id === active.id;
          return (
            <button
              key={section.id}
              onClick={() => setActiveId(section.id)}
              className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-widest transition-colors"
              style={{
                backgroundColor: isActive ? `${accent}22` : 'transparent',
                color: isActive ? accent : muted,
              }}
            >
              {section.label}
            </button>
          );
        })}
      </div>
      <div className="px-3 pt-3 pb-1 flex items-baseline gap-3">
        <span className="text-sm font-bold truncate" style={{ color: accent }}>{active.label}</span>
        <span className="text-xs font-medium truncate" style={{ color: muted }}>{active.subtitle}</span>
        <span className="ml-auto text-[10px] tabular-nums shrink-0" style={{ color: muted }}>
          {ownedInSlice(slice)}/{slice.length}
        </span>
      </div>
      <StickerBlock stickers={slice} columns={columns} theme={theme} cellProps={cellProps} />
    </div>
  );
}

function CarouselLayout({ stickers, columns, theme, cellProps, accent, muted, activeIdx, setActiveIdx }) {
  const participating = cellProps?.participatingStickers;
  const sections = ALBUM_SECTIONS.filter(s => rangeParticipates(participating, s.from, s.to));
  const active = sections[activeIdx] || sections[0];
  const slice = sliceSection(stickers, active);

  const prev = () => setActiveIdx((activeIdx - 1 + sections.length) % sections.length);
  const next = () => setActiveIdx((activeIdx + 1) % sections.length);

  return (
    <div>
      <div className="flex items-center justify-between px-3 pt-4 pb-2">
        <button onClick={prev} className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                style={{ backgroundColor: `${accent}11`, color: accent }}>
          <span className="material-symbols-outlined text-lg">chevron_left</span>
        </button>
        <div className="flex flex-col items-center text-center min-w-0">
          <span className="text-sm font-black uppercase tracking-widest" style={{ color: accent }}>{active.label}</span>
          <span className="text-xs font-medium truncate max-w-[480px]" style={{ color: muted }}>{active.subtitle}</span>
          <span className="text-[10px] mt-0.5 tabular-nums" style={{ color: muted }}>
            #{active.from}–{active.to} · {ownedInSlice(slice)}/{slice.length} owned
          </span>
        </div>
        <button onClick={next} className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                style={{ backgroundColor: `${accent}11`, color: accent }}>
          <span className="material-symbols-outlined text-lg">chevron_right</span>
        </button>
      </div>
      <StickerBlock stickers={slice} columns={columns} theme={theme} cellProps={cellProps} />
      <div className="flex items-center justify-center gap-1.5 py-3">
        {sections.map((s, i) => {
          const isActive = i === activeIdx;
          return (
            <button key={s.id} onClick={() => setActiveIdx(i)} className="rounded-full transition-all"
                    style={{ width: isActive ? 18 : 6, height: 6, backgroundColor: isActive ? accent : `${muted}55` }} />
          );
        })}
      </div>
    </div>
  );
}

function RowsLayout({ stickers, theme, cellProps, accent, muted }) {
  const participating = cellProps?.participatingStickers;
  return (
    <div>
      {ALBUM_SECTIONS.filter(s => rangeParticipates(participating, s.from, s.to)).map(section => {
        const slice = sliceSection(stickers, section);
        if (slice.length === 0) return null;
        return (
          <div key={section.id} className="mb-2">
            <SectionHeader section={section} slice={slice} accent={accent} muted={muted} dense />
            <div className="flex gap-1 overflow-x-auto hide-scrollbar px-3 pb-3"
                 style={{ backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)' }}>
              {slice.map(s => (
                <div key={s.n} className="shrink-0" style={{ width: 36, height: 36 }}>
                  <StickerCell sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AccordionLayout({ stickers, columns, theme, cellProps, accent, muted, expanded, setExpanded }) {
  const participating = cellProps?.participatingStickers;
  return (
    <div>
      {ALBUM_SECTIONS.filter(s => rangeParticipates(participating, s.from, s.to)).map(section => {
        const slice = sliceSection(stickers, section);
        if (slice.length === 0) return null;
        const isOpen = expanded.has(section.id);
        return (
          <div key={section.id} className="mb-1">
            <button
              onClick={() => {
                const next = new Set(expanded);
                if (next.has(section.id)) next.delete(section.id); else next.add(section.id);
                setExpanded(next);
              }}
              className="w-full flex items-baseline gap-3 px-3 py-2 transition-colors"
              style={{ backgroundColor: isOpen ? `${accent}11` : 'transparent' }}
            >
              <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest"
                    style={{ backgroundColor: `${accent}22`, color: accent }}>
                {section.label}
              </span>
              <span className="text-xs font-medium truncate" style={{ color: muted }}>{section.subtitle}</span>
              <span className="ml-auto text-[10px] tabular-nums shrink-0" style={{ color: muted }}>
                {ownedInSlice(slice)}/{slice.length}
              </span>
              <span className="material-symbols-outlined text-base shrink-0" style={{ color: muted }}>
                {isOpen ? 'expand_less' : 'expand_more'}
              </span>
            </button>
            {isOpen && <StickerBlock stickers={slice} columns={columns} theme={theme} cellProps={cellProps} />}
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   NEW LAYOUTS — country-aware
   ════════════════════════════════════════════════════════════════════ */

/* 6 — By Country: each section expands into 4 country sub-blocks */
function ByCountryLayout({ stickers, columns, theme, cellProps, accent, muted }) {
  const participating = cellProps?.participatingStickers;
  return (
    <div>
      {ALBUM_SECTIONS.filter(s => rangeParticipates(participating, s.from, s.to)).map(section => {
        const slice = sliceSection(stickers, section);
        if (slice.length === 0) return null;
        const countries = getSectionCountries(section);
        if (!countries) {
          // FWC — no country split
          return (
            <div key={section.id}>
              <SectionHeader section={section} slice={slice} accent={accent} muted={muted} />
              <StickerBlock stickers={slice} columns={columns} theme={theme} cellProps={cellProps} />
            </div>
          );
        }
        return (
          <div key={section.id}>
            <SectionHeader section={section} slice={slice} accent={accent} muted={muted} />
            {countries.map(country => {
              const cSlice = sliceRange(stickers, country.from, country.to);
              if (cSlice.length === 0) return null;
              return (
                <div key={country.name}>
                  <CountryHeader country={country} slice={cSlice} muted={muted} dense />
                  <StickerBlock stickers={cSlice} columns={columns} theme={theme} cellProps={cellProps} padding="px-3 pb-2" />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/* 7 — Country Cards: each country becomes a card with a flag color band on top
   and a 5×4 mini-grid of stickers inside. Cards arranged in a responsive grid.
   `cardsPerRow` controls the desktop-xl column count (4 or 5). */
function CountryCardsLayout({ stickers, theme, cellProps, accent, muted, cardsPerRow = 4 }) {
  const participating = cellProps?.participatingStickers;
  const allCountries = ALBUM_SECTIONS.flatMap(section => {
    const countries = getSectionCountries(section);
    if (!countries) {
      if (!rangeParticipates(participating, section.from, section.to)) return [];
      return [{ kind: 'fwc', section, slice: sliceSection(stickers, section) }];
    }
    return countries
      .filter(c => rangeParticipates(participating, c.from, c.to))
      .map(c => ({ kind: 'country', country: c, slice: sliceRange(stickers, c.from, c.to), section }));
  });

  // Step intermedio en desktop-xl (1280-1439) para que las cards no queden
  // apretadas a 5 cols cuando el viewport recién entra a desktop-xl. A
  // partir de desktop-xxl (1440+) suben a su cardsPerRow nominal.
  const xlCols = cardsPerRow === 5
    ? 'desktop-xl:grid-cols-4 desktop-xxl:grid-cols-5'
    : cardsPerRow === 3
      ? 'desktop-xl:grid-cols-3'
      : 'desktop-xl:grid-cols-3 desktop-xxl:grid-cols-4';

  return (
    <div className={`px-2 mobile-md:px-3 py-2 mobile-md:py-3 grid grid-cols-1 mobile-md:grid-cols-2 tablet-md:grid-cols-3 ${xlCols} gap-2 mobile-md:gap-3`}>
      {allCountries.map((entry, i) => {
        const slice = entry.slice;
        const owned = ownedInSlice(slice);
        const total = slice.length;
        const pct = total ? Math.round((owned / total) * 100) : 0;
        const color = entry.kind === 'country' ? entry.country.color : accent;
        const label = entry.kind === 'country' ? entry.country.code : entry.section.label;
        const fullName = entry.kind === 'country' ? entry.country.name : entry.section.subtitle;
        return (
          <div key={i} data-album-card={label} className="rounded-xl overflow-hidden"
               style={{ backgroundColor: theme?.gridBg ?? '#ffffff', border: `1px solid ${muted}22` }}>
            {/* flag color stripe */}
            <div className="h-1.5 w-full" style={{ backgroundColor: color }} />
            <div className="px-3 py-2 flex items-start gap-2">
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <span className="text-base font-black tracking-wide leading-tight" style={{ color }}>{label}</span>
                <span className="text-[11px] font-medium break-words leading-tight" style={{ color: muted }}>{fullName}</span>
              </div>
              <span className="text-[10px] font-bold tabular-nums shrink-0 mt-0.5" style={{ color: muted }}>{owned}/{total} · {pct}%</span>
            </div>
            <div className="px-3 pb-3"
                 style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 3 }}>
              {slice.map(s => (
                <StickerCell key={s.n} sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* 8 — Country Roster: one horizontal-scroll row per country, like team strips */
function CountryRosterLayout({ stickers, theme, cellProps, accent, muted }) {
  const participating = cellProps?.participatingStickers;
  const allCountries = ALBUM_SECTIONS.flatMap(section => {
    const countries = getSectionCountries(section);
    if (!countries) {
      if (!rangeParticipates(participating, section.from, section.to)) return [];
      return [{ kind: 'fwc', section, slice: sliceSection(stickers, section) }];
    }
    return countries
      .filter(c => rangeParticipates(participating, c.from, c.to))
      .map(c => ({ kind: 'country', country: c, slice: sliceRange(stickers, c.from, c.to), section }));
  });
  return (
    <div>
      {allCountries.map((entry, i) => {
        const slice = entry.slice;
        const color = entry.kind === 'country' ? entry.country.color : accent;
        const code = entry.kind === 'country' ? entry.country.code : entry.section.label;
        const name = entry.kind === 'country' ? entry.country.name : entry.section.subtitle;
        const owned = ownedInSlice(slice);
        return (
          <div key={i} className="flex items-center gap-3 px-3 py-1 hover:bg-black/[0.02]">
            <div className="flex items-center gap-2 w-44 shrink-0">
              <span className="w-1 h-7 rounded-sm shrink-0" style={{ backgroundColor: color }} />
              <div className="min-w-0">
                <div className="text-[11px] font-black tracking-wide" style={{ color }}>{code}</div>
                <div className="text-[10px] truncate" style={{ color: muted }}>{name}</div>
              </div>
              <div className="ml-auto text-[10px] tabular-nums shrink-0" style={{ color: muted }}>
                {owned}/{slice.length}
              </div>
            </div>
            <div className="flex gap-1 overflow-x-auto hide-scrollbar flex-1 py-1">
              {slice.map(s => (
                <div key={s.n} className="shrink-0" style={{ width: 30, height: 30 }}>
                  <StickerCell sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   NEW LAYOUTS — density variations
   ════════════════════════════════════════════════════════════════════ */

/* 9 — Compact Dense: 50 columns, ultra-tight grid (bird's-eye view) */
function CompactDenseLayout({ stickers, theme, cellProps }) {
  return (
    <div className="p-3"
         style={{
           display: 'grid',
           gridTemplateColumns: 'repeat(50, minmax(0, 1fr))',
           gap: 2,
           backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)',
         }}>
      {stickers.map(s => (
        <div key={s.n} style={{ aspectRatio: '1 / 1', minWidth: 0 }}>
          <StickerCell sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
        </div>
      ))}
    </div>
  );
}

/* 10 — Showcase: 8 columns, big cells (slow browsing) */
function ShowcaseLayout({ stickers, theme, cellProps }) {
  return (
    <div className="p-4"
         style={{
           display: 'grid',
           gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
           gap: 8,
           backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)',
         }}>
      {stickers.map(s => (
        <div key={s.n} style={{ fontSize: 18, aspectRatio: '1 / 1' }}>
          <StickerCell sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
        </div>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   NEW LAYOUTS — info overlays per cell
   ════════════════════════════════════════════════════════════════════ */

/* 11 — With Country Codes: standard grid, each cell shows its country code */
function WithCodesLayout({ stickers, theme, cellProps }) {
  const { viewMode, selected, hideRepeated, extras, onCellClick } = cellProps;
  return (
    <div className="p-3"
         style={{
           display: 'grid',
           gridTemplateColumns: 'repeat(20, minmax(0, 1fr))',
           gap: 4,
           backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)',
         }}>
      {stickers.map(s => {
        const { style } = resolveStickerStyle(s, viewMode, selected, extras, cellProps.theme);
        const info = countryForSticker(s.n);
        const code = info?.code || '';
        return (
          <div key={s.n}
               onClick={() => onCellClick(s)}
               style={{ ...style, flexDirection: 'column', justifyContent: 'center', fontSize: 11, gap: 1 }}>
            <span style={{ fontWeight: 800, lineHeight: 1 }}>{s.n}</span>
            <span style={{ fontSize: 7, fontWeight: 700, opacity: 0.75, letterSpacing: 0.5 }}>{code}</span>
          </div>
        );
      })}
    </div>
  );
}

/* 12 — With Player Names: roster-style 6 cols with mock names */
function WithNamesLayout({ stickers, theme, cellProps, accent, muted }) {
  const { viewMode, selected, hideRepeated, extras, onCellClick } = cellProps;
  return (
    <div className="p-3"
         style={{
           display: 'grid',
           gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
           gap: 6,
           backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)',
         }}>
      {stickers.map(s => {
        const { style } = resolveStickerStyle(s, viewMode, selected, extras, cellProps.theme);
        const info = countryForSticker(s.n);
        const code = info?.code || '';
        const name = mockPlayerName(s.n);
        const isOwned = s.owned;
        const baseBg = style.backgroundColor;
        return (
          <div key={s.n}
               onClick={() => onCellClick(s)}
               className="rounded-md cursor-pointer transition-all flex items-center gap-2 px-2 py-1.5"
               style={{
                 backgroundColor: isOwned ? baseBg : 'transparent',
                 border: isOwned ? '1px solid transparent' : `1px dashed ${style.color}66`,
                 color: style.color,
                 outline: style.outline,
                 outlineOffset: style.outlineOffset,
                 boxShadow: style.boxShadow,
               }}>
            <div className="flex flex-col items-center justify-center w-7 h-7 rounded shrink-0"
                 style={{ backgroundColor: 'rgba(0,0,0,0.08)' }}>
              <span className="text-[10px] font-black tabular-nums leading-none">{s.n}</span>
              <span className="text-[7px] font-bold opacity-70 leading-none mt-0.5">{code}</span>
            </div>
            <span className="text-[11px] font-bold truncate flex-1" style={{ color: style.color }}>{name}</span>
            {s.reps >= 2 && (
              <span className="text-[9px] font-black tabular-nums px-1.5 py-0.5 rounded shrink-0"
                    style={{ backgroundColor: 'rgba(0,0,0,0.18)', color: 'inherit' }}>
                ×{s.reps}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* 13 — With Mock Photos: each cell is a gradient circle "portrait" with a
   number badge in the corner. */
function WithPhotosLayout({ stickers, theme, cellProps }) {
  const { viewMode, selected, hideRepeated, extras, onCellClick } = cellProps;
  return (
    <div className="p-3"
         style={{
           display: 'grid',
           gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
           gap: 6,
           backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)',
         }}>
      {stickers.map(s => {
        const { style } = resolveStickerStyle(s, viewMode, selected, extras, cellProps.theme);
        const photo = mockPhotoStyle(s.n);
        const isSelected = selected?.has(s.n);
        return (
          <div key={s.n}
               onClick={() => onCellClick(s)}
               className="relative cursor-pointer transition-transform hover:scale-105"
               style={{
                 aspectRatio: '1 / 1',
                 borderRadius: '50%',
                 ...photo,
                 opacity: s.owned ? 1 : 0.18,
                 outline: isSelected ? `3px solid ${style.outline || '#000'}` : 'none',
                 outlineOffset: -2,
                 filter: s.owned ? 'none' : 'grayscale(50%)',
               }}>
            <span className="absolute bottom-0 right-0 text-[9px] font-black tabular-nums px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: 'rgba(0,0,0,0.7)', color: '#fff' }}>
              {s.n}
            </span>
            {s.reps >= 2 && (
              <span className="absolute top-0 right-0 text-[8px] font-black tabular-nums px-1 rounded-full"
                    style={{ backgroundColor: '#ef4444', color: '#fff' }}>
                ×{s.reps}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   NEW LAYOUTS — smart filters
   ════════════════════════════════════════════════════════════════════ */

/* 14 — Missing Only: filters to un-owned stickers, larger cells */
function MissingOnlyLayout({ stickers, theme, cellProps, accent, muted }) {
  const missing = stickers.filter(s => !s.owned);
  if (missing.length === 0) {
    return (
      <div className="px-6 py-16 text-center">
        <span className="material-symbols-outlined text-5xl mb-4 block" style={{ color: accent }}>verified</span>
        <h3 className="text-lg font-black" style={{ color: accent }}>Album complete!</h3>
        <p className="text-sm mt-1" style={{ color: muted }}>You own every sticker.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="px-3 pt-4 pb-2 flex items-baseline gap-3">
        <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest"
              style={{ backgroundColor: `${accent}22`, color: accent }}>Missing</span>
        <span className="text-xs font-medium" style={{ color: muted }}>{missing.length} stickers to find</span>
      </div>
      <div className="p-3"
           style={{
             display: 'grid',
             gridTemplateColumns: 'repeat(15, minmax(0, 1fr))',
             gap: 6,
             backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)',
           }}>
        {missing.map(s => (
          <div key={s.n} style={{ fontSize: 13, aspectRatio: '1 / 1' }}>
            <StickerCell sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* 15 — Repeated Only: filters to stickers with reps>=2, trade-focus mode */
function RepeatedOnlyLayout({ stickers, theme, cellProps, accent, muted }) {
  const repeated = stickers.filter(s => s.reps >= 2);
  if (repeated.length === 0) {
    return (
      <div className="px-6 py-16 text-center">
        <span className="material-symbols-outlined text-5xl mb-4 block" style={{ color: muted }}>swap_horiz</span>
        <h3 className="text-lg font-black" style={{ color: muted }}>No duplicates yet</h3>
        <p className="text-sm mt-1" style={{ color: muted }}>Once you have repeats they'll show up here for trading.</p>
      </div>
    );
  }
  // sort by reps desc — most spare first
  const sorted = [...repeated].sort((a, b) => b.reps - a.reps);
  const totalSpare = sorted.reduce((acc, s) => acc + (s.reps - 1), 0);
  return (
    <div>
      <div className="px-3 pt-4 pb-2 flex items-baseline gap-3">
        <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest"
              style={{ backgroundColor: `${accent}22`, color: accent }}>Spare</span>
        <span className="text-xs font-medium" style={{ color: muted }}>
          {sorted.length} stickers · {totalSpare} extras to trade
        </span>
      </div>
      <div className="p-3"
           style={{
             display: 'grid',
             gridTemplateColumns: 'repeat(15, minmax(0, 1fr))',
             gap: 6,
             backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)',
           }}>
        {sorted.map(s => (
          <div key={s.n} style={{ fontSize: 13, aspectRatio: '1 / 1' }}>
            <StickerCell sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* 16 — Heatmap: color saturation based on rep count (0=missing, 1=owned, 2+=spares) */
function HeatmapLayout({ stickers, theme, cellProps, accent }) {
  const { viewMode, selected, extras, onCellClick } = cellProps;
  // Convert hex accent to rgb for blending
  const rgb = hexToRgb(accent);
  return (
    <div className="p-3"
         style={{
           display: 'grid',
           gridTemplateColumns: 'repeat(30, minmax(0, 1fr))',
           gap: 2,
           backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)',
         }}>
      {stickers.map(s => {
        const isSelected = selected?.has(s.n);
        // 0 reps + not owned = 0; owned + 0 reps = 1; reps>=2 = scaled
        let intensity;
        if (!s.owned) intensity = 0;
        else if (s.reps === 0) intensity = 0.40;
        else intensity = Math.min(1, 0.55 + (s.reps - 1) * 0.18);
        const bg = intensity === 0
          ? 'transparent'
          : `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${intensity})`;
        const border = intensity === 0
          ? `1px dashed rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.45)`
          : '1px solid transparent';
        return (
          <div key={s.n}
               onClick={() => onCellClick(s)}
               className="relative cursor-pointer transition-all"
               style={{
                 aspectRatio: '1 / 1',
                 backgroundColor: bg,
                 border,
                 fontSize: 10,
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 fontWeight: 700,
                 color: intensity > 0.65 ? '#fff' : accent,
                 outline: isSelected ? `2px solid ${accent}` : 'none',
                 outlineOffset: -1,
               }}>
            {s.n}
          </div>
        );
      })}
    </div>
  );
}

function hexToRgb(hex) {
  if (typeof hex !== 'string') return { r: 16, g: 185, b: 129 };
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})/i);
  if (!m) return { r: 16, g: 185, b: 129 };
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}

/* ════════════════════════════════════════════════════════════════════
   NEW LAYOUTS — creative / risky
   ════════════════════════════════════════════════════════════════════ */

/* 17 — Hexagonal Honeycomb: hex-shaped cells in offset rows. */
function HexagonalLayout({ stickers, theme, cellProps }) {
  const { viewMode, selected, extras, onCellClick } = cellProps;
  const PER_ROW = 30;
  const rows = [];
  for (let i = 0; i < stickers.length; i += PER_ROW) rows.push(stickers.slice(i, i + PER_ROW));

  return (
    <div className="p-3"
         style={{ backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)' }}>
      {rows.map((row, ri) => (
        <div key={ri}
             className="flex items-center"
             style={{
               marginLeft: ri % 2 === 1 ? 'calc(100% / 60)' : 0,
               marginTop: ri === 0 ? 0 : -6,
             }}>
          {row.map(s => {
            const { style } = resolveStickerStyle(s, viewMode, selected, extras, cellProps.theme);
            const isSelected = selected?.has(s.n);
            return (
              <div key={s.n}
                   onClick={() => onCellClick(s)}
                   style={{
                     flex: '0 0 calc(100% / 30)',
                     aspectRatio: '1 / 1.1547', // hex aspect ratio
                     clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                     backgroundColor: style.backgroundColor,
                     border: 'none',
                     outline: 'none',
                     color: style.color,
                     fontSize: 9,
                     fontWeight: 700,
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     cursor: 'pointer',
                     transition: 'transform 0.15s',
                     transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                     filter: isSelected ? `drop-shadow(0 0 4px ${style.outline || 'rgba(0,0,0,0.4)'})` : 'none',
                   }}>
                {s.n}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* 18 — Booklet: two-page spread. Left page = sections 1-7, right = 8-13. */
function BookletLayout({ stickers, columns, theme, cellProps, accent, muted }) {
  const left = ALBUM_SECTIONS.slice(0, 7);
  const right = ALBUM_SECTIONS.slice(7);

  const Page = ({ sections, title }) => (
    <div className="rounded-lg overflow-hidden flex-1 min-w-0"
         style={{
           background: 'linear-gradient(135deg, #fefefe 0%, #f3f4f6 100%)',
           border: `1px solid ${muted}33`,
           boxShadow: '0 4px 20px rgba(0,0,0,0.08), inset 0 0 30px rgba(0,0,0,0.02)',
         }}>
      <div className="px-4 pt-3 pb-2 flex items-baseline gap-2 border-b" style={{ borderColor: `${muted}22` }}>
        <span className="text-xs font-black uppercase tracking-widest" style={{ color: accent }}>{title}</span>
      </div>
      <div className="px-2 py-2">
        {sections.map(section => {
          const slice = sliceSection(stickers, section);
          if (slice.length === 0) return null;
          return (
            <div key={section.id}>
              <div className="flex items-baseline gap-2 px-2 pt-2 pb-1">
                <span className="text-[10px] font-black uppercase tracking-widest"
                      style={{ color: accent }}>{section.label}</span>
                <span className="text-[10px] truncate" style={{ color: muted }}>{section.subtitle}</span>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.max(20, columns - 10)}, minmax(0, 1fr))`,
                gap: 2,
                padding: '0 6px 4px',
              }}>
                {slice.map(s => (
                  <StickerCell key={s.n} sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col tablet-md:flex-row gap-3 p-3">
      <Page sections={left} title="Page 1" />
      <Page sections={right} title="Page 2" />
    </div>
  );
}

/* 19 — Pitch Formation: each country shown on a soccer pitch with stickers
   placed in 4-3-3 + bench. One country at a time, navigated like a carousel. */
function PitchLayout({ stickers, theme, cellProps, accent, muted, pitchIdx, setPitchIdx }) {
  const allCountries = useMemo(() => ALBUM_SECTIONS.flatMap(section => {
    const countries = getSectionCountries(section);
    if (!countries) return [{ kind: 'fwc', section }];
    return countries.map(c => ({ kind: 'country', country: c, section }));
  }), []);
  const entry = allCountries[pitchIdx] || allCountries[0];
  const slice = entry.kind === 'country'
    ? sliceRange(stickers, entry.country.from, entry.country.to)
    : sliceSection(stickers, entry.section);
  const color = entry.kind === 'country' ? entry.country.color : accent;
  const code = entry.kind === 'country' ? entry.country.code : 'FWC';
  const name = entry.kind === 'country' ? entry.country.name : entry.section.subtitle;
  const owned = ownedInSlice(slice);

  // Field positions for 11 starters (4-3-3) — y from top in % of pitch height.
  // x in % of pitch width (0=left/our goal, 100=right/their goal).
  const formation = [
    { x: 50, y: 88 },                                           // GK
    { x: 16, y: 70 }, { x: 38, y: 72 }, { x: 62, y: 72 }, { x: 84, y: 70 },   // Back 4
    { x: 25, y: 50 }, { x: 50, y: 52 }, { x: 75, y: 50 },                      // Mid 3
    { x: 22, y: 25 }, { x: 50, y: 18 }, { x: 78, y: 25 },                      // Forward 3
  ];
  const starters = slice.slice(0, 11);
  const bench = slice.slice(11);
  const prev = () => setPitchIdx((pitchIdx - 1 + allCountries.length) % allCountries.length);
  const next = () => setPitchIdx((pitchIdx + 1) % allCountries.length);

  return (
    <div className="p-3">
      <div className="flex items-center gap-3 px-1 pb-2">
        <button onClick={prev} className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${accent}11`, color: accent }}>
          <span className="material-symbols-outlined text-base">chevron_left</span>
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-1.5 h-7 rounded-sm shrink-0" style={{ backgroundColor: color }} />
          <div>
            <div className="text-sm font-black" style={{ color }}>{code} · {name}</div>
            <div className="text-[10px] tabular-nums" style={{ color: muted }}>{owned}/{slice.length} owned</div>
          </div>
        </div>
        <button onClick={next} className="ml-auto w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${accent}11`, color: accent }}>
          <span className="material-symbols-outlined text-base">chevron_right</span>
        </button>
      </div>

      {/* Pitch */}
      <div className="relative rounded-lg overflow-hidden"
           style={{
             aspectRatio: '7 / 10',
             maxWidth: 560,
             margin: '0 auto',
             background: `linear-gradient(180deg, #1f8a3a 0%, #258d3d 50%, #1f8a3a 100%)`,
             backgroundImage: `repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 30px, transparent 30px, transparent 60px), linear-gradient(180deg, #1f8a3a 0%, #258d3d 50%, #1f8a3a 100%)`,
           }}>
        {/* Pitch lines */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 142" preserveAspectRatio="none">
          <rect x="2" y="2" width="96" height="138" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <line x1="2" y1="71" x2="98" y2="71" stroke="white" strokeWidth="0.3" opacity="0.7" />
          <circle cx="50" cy="71" r="9" fill="none" stroke="white" strokeWidth="0.3" opacity="0.7" />
          <circle cx="50" cy="71" r="0.8" fill="white" opacity="0.7" />
          <rect x="20" y="2" width="60" height="14" fill="none" stroke="white" strokeWidth="0.3" opacity="0.7" />
          <rect x="20" y="126" width="60" height="14" fill="none" stroke="white" strokeWidth="0.3" opacity="0.7" />
          <rect x="35" y="2" width="30" height="6" fill="none" stroke="white" strokeWidth="0.3" opacity="0.7" />
          <rect x="35" y="134" width="30" height="6" fill="none" stroke="white" strokeWidth="0.3" opacity="0.7" />
        </svg>
        {/* Players */}
        {starters.map((s, idx) => {
          const pos = formation[idx];
          if (!pos) return null;
          return (
            <div key={s.n}
                 className="absolute"
                 style={{
                   left: `${pos.x}%`,
                   top: `${pos.y}%`,
                   transform: 'translate(-50%, -50%)',
                   width: 38, height: 38,
                 }}>
              <StickerCell sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
            </div>
          );
        })}
      </div>

      {bench.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: muted }}>
            Bench ({bench.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {bench.map(s => (
              <div key={s.n} style={{ width: 32, height: 32 }}>
                <StickerCell sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* 20 — Timeline Strip: single horizontal scroll with section dividers */
function TimelineLayout({ stickers, theme, cellProps, accent, muted }) {
  return (
    <div className="p-3">
      <div className="text-[10px] font-black uppercase tracking-widest mb-2 px-1" style={{ color: muted }}>
        Timeline · scroll horizontally to walk the album
      </div>
      <div className="flex items-stretch overflow-x-auto hide-scrollbar pb-3"
           style={{ backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)', borderRadius: 8 }}>
        {ALBUM_SECTIONS.filter(s => rangeParticipates(cellProps?.participatingStickers, s.from, s.to)).map(section => {
          const slice = sliceSection(stickers, section);
          if (slice.length === 0) return null;
          return (
            <div key={section.id} className="shrink-0 px-2"
                 style={{ borderRight: `2px dashed ${muted}33` }}>
              <div className="text-center pt-2 pb-1">
                <div className="inline-block px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest"
                     style={{ backgroundColor: `${accent}22`, color: accent }}>
                  {section.label}
                </div>
                <div className="text-[9px] mt-1" style={{ color: muted }}>
                  {ownedInSlice(slice)}/{slice.length}
                </div>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(10, 28px)',
                gap: 2,
                padding: '4px 4px',
              }}>
                {slice.map(s => (
                  <div key={s.n} style={{ width: 28, height: 28, fontSize: 9 }}>
                    <StickerCell sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Main switch
   ════════════════════════════════════════════════════════════════════ */

function AlbumGrid({ stickers, viewMode, selected, hideRepeated, extras, onCellClick, columns, theme, layout = 'grid', participatingStickers }) {
  const cellProps = { viewMode, selected, hideRepeated, extras, onCellClick, theme, participatingStickers };
  const accent = theme?.primaryAccent ?? '#10B981';
  const muted  = theme?.mutedText     ?? '#6b7280';

  const [activeId, setActiveId] = useState(ALBUM_SECTIONS[0].id);
  const [activeIdx, setActiveIdx] = useState(0);
  const [pitchIdx, setPitchIdx] = useState(0);
  const [expanded, setExpanded] = useState(() => new Set([ALBUM_SECTIONS[0].id]));

  useEffect(() => {
    if (layout === 'tabs') setActiveId(ALBUM_SECTIONS[0].id);
    if (layout === 'carousel') setActiveIdx(0);
    if (layout === 'pitch') setPitchIdx(0);
  }, [layout]);

  const selectedNumbers = useMemo(() => [...(selected || [])], [selected]);
  useEffect(() => {
    if (selectedNumbers.length === 0) return;
    const first = selectedNumbers[0];
    const idx = ALBUM_SECTIONS.findIndex(s => first >= s.from && first <= s.to);
    if (idx === -1) return;
    if (layout === 'tabs') setActiveId(ALBUM_SECTIONS[idx].id);
    if (layout === 'carousel') setActiveIdx(idx);
    if (layout === 'accordion') setExpanded(prev => {
      if (prev.has(ALBUM_SECTIONS[idx].id)) return prev;
      const next = new Set(prev); next.add(ALBUM_SECTIONS[idx].id); return next;
    });
  }, [selectedNumbers, layout]);

  switch (layout) {
    case 'sections':       return <SectionsLayout stickers={stickers} columns={columns} theme={theme} cellProps={cellProps} accent={accent} muted={muted} />;
    case 'tabs':           return <TabsLayout stickers={stickers} columns={columns} theme={theme} cellProps={cellProps} accent={accent} muted={muted} activeId={activeId} setActiveId={setActiveId} />;
    case 'carousel':       return <CarouselLayout stickers={stickers} columns={columns} theme={theme} cellProps={cellProps} accent={accent} muted={muted} activeIdx={activeIdx} setActiveIdx={setActiveIdx} />;
    case 'rows':           return <RowsLayout stickers={stickers} theme={theme} cellProps={cellProps} accent={accent} muted={muted} />;
    case 'accordion':      return <AccordionLayout stickers={stickers} columns={columns} theme={theme} cellProps={cellProps} accent={accent} muted={muted} expanded={expanded} setExpanded={setExpanded} />;
    case 'by-country':     return <ByCountryLayout stickers={stickers} columns={columns} theme={theme} cellProps={cellProps} accent={accent} muted={muted} />;
    case 'country-cards':   return <CountryCardsLayout stickers={stickers} theme={theme} cellProps={cellProps} accent={accent} muted={muted} cardsPerRow={4} />;
    case 'country-cards-3': return <CountryCardsLayout stickers={stickers} theme={theme} cellProps={cellProps} accent={accent} muted={muted} cardsPerRow={3} />;
    case 'country-cards-5': return <CountryCardsLayout stickers={stickers} theme={theme} cellProps={cellProps} accent={accent} muted={muted} cardsPerRow={5} />;
    case 'country-roster': return <CountryRosterLayout stickers={stickers} theme={theme} cellProps={cellProps} accent={accent} muted={muted} />;
    case 'compact':        return <CompactDenseLayout stickers={stickers} theme={theme} cellProps={cellProps} />;
    case 'showcase':       return <ShowcaseLayout stickers={stickers} theme={theme} cellProps={cellProps} />;
    case 'with-codes':     return <WithCodesLayout stickers={stickers} theme={theme} cellProps={cellProps} />;
    case 'with-names':     return <WithNamesLayout stickers={stickers} theme={theme} cellProps={cellProps} accent={accent} muted={muted} />;
    case 'with-photos':    return <WithPhotosLayout stickers={stickers} theme={theme} cellProps={cellProps} />;
    case 'missing-only':   return <MissingOnlyLayout stickers={stickers} theme={theme} cellProps={cellProps} accent={accent} muted={muted} />;
    case 'repeated-only':  return <RepeatedOnlyLayout stickers={stickers} theme={theme} cellProps={cellProps} accent={accent} muted={muted} />;
    case 'heatmap':        return <HeatmapLayout stickers={stickers} theme={theme} cellProps={cellProps} accent={accent} />;
    case 'hex':            return <HexagonalLayout stickers={stickers} theme={theme} cellProps={cellProps} />;
    case 'booklet':        return <BookletLayout stickers={stickers} columns={columns} theme={theme} cellProps={cellProps} accent={accent} muted={muted} />;
    case 'pitch':          return <PitchLayout stickers={stickers} theme={theme} cellProps={cellProps} accent={accent} muted={muted} pitchIdx={pitchIdx} setPitchIdx={setPitchIdx} />;
    case 'timeline':       return <TimelineLayout stickers={stickers} theme={theme} cellProps={cellProps} accent={accent} muted={muted} />;
    default:               return <FlatGrid stickers={stickers} columns={columns} theme={theme} cellProps={cellProps} />;
  }
}

export default AlbumGrid;
